import React, { useState, useMemo } from 'react';
import { Box, Typography, Alert, Tabs, Tab, Chip } from '@mui/material';
import { GridComponent, ColumnsDirective, ColumnDirective, Page, Sort, Inject, SortSettingsModel, PageSettingsModel, RowSelectEventArgs } from '@syncfusion/ej2-react-grids';
import ViewDocumentDialog from './ViewDocumentDialog';
import { Person, CalendarToday, Receipt, Photo, Assessment, Description } from '@mui/icons-material';
import { EntityDocumentView } from './types';

interface DocumentsGridProps {
  entityId: string;
  documents: EntityDocumentView[] | undefined;
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  currentUserName: string;
  onDownload: (documentId: string) => Promise<Blob>;
  onDelete?: (documentId: string) => Promise<void>;
  className?: string;
  /** When true, no upload or delete option is ever offered. */
  readOnly?: boolean;
}

interface DocumentGridRow {
  id: string;
  fileName: string;
  extension: string;
  uploadBy: string;
  uploadDate: string;
  uploadDateFormatted: string;
  fileType: string;
  iconEmoji: string;
  documentType: string;
  document: EntityDocumentView;
}

const getFileIcon = (extension: string) => {
  const ext = extension.toLowerCase();
  if (['.pdf'].includes(ext)) {
    return '📄';
  } else if (['.doc', '.docx'].includes(ext)) {
    return '📝';
  } else if (['.xls', '.xlsx'].includes(ext)) {
    return '📊';
  } else if (['.png', '.jpg', '.jpeg', '.gif'].includes(ext)) {
    return '🖼️';
  } else if (['.txt'].includes(ext)) {
    return '📋';
  }
  return '📎';
};

const FILE_TYPE_LABELS: Record<string, string> = {
  '.pdf': 'PDF Document',
  '.doc': 'Word Document',
  '.docx': 'Word Document',
  '.xls': 'Excel Spreadsheet',
  '.xlsx': 'Excel Spreadsheet',
  '.png': 'Image File',
  '.jpg': 'Image File',
  '.jpeg': 'Image File',
  '.gif': 'Image File',
  '.txt': 'Text File',
};

const getFileTypeLabel = (extension: string): string =>
  FILE_TYPE_LABELS[extension.toLowerCase()] || 'Document';

const formatDate = (dateString?: string | null): string => {
  if (!dateString) return 'Unknown';
  try {
    return new Date(dateString).toLocaleString();
  } catch {
    return 'Unknown';
  }
};

const getDocumentTypeIcon = (type: string) => {
  const typeUpper = (type || 'General').toUpperCase();
  switch (typeUpper) {
    case 'QUOTE': return <Receipt sx={{ fontSize: 14 }} />;
    case 'INVOICE': return <Receipt sx={{ fontSize: 14 }} />;
    case 'PHOTO': return <Photo sx={{ fontSize: 14 }} />;
    case 'REPORT': return <Assessment sx={{ fontSize: 14 }} />;
    default: return <Description sx={{ fontSize: 14 }} />;
  }
};

const getDocumentTypeColor = (type: string): { bg: string; color: string } => {
  const typeUpper = (type || 'General').toUpperCase();
  switch (typeUpper) {
    case 'QUOTE': return { bg: '#dbeafe', color: '#1e40af' };
    case 'INVOICE': return { bg: '#fef3c7', color: '#92400e' };
    case 'PHOTO': return { bg: '#fce7f3', color: '#9f1239' };
    case 'REPORT': return { bg: '#d1fae5', color: '#065f46' };
    default: return { bg: '#e5e7eb', color: '#374151' };
  }
};

type DocumentTypeFilter = 'all' | 'quotes' | 'photos' | 'other';

const normalizeDocumentType = (type?: string | null): string => (type || 'General').toUpperCase();

const matchesFilterTab = (doc: EntityDocumentView, filterTab: DocumentTypeFilter): boolean => {
  const docType = normalizeDocumentType(doc.documentType);
  switch (filterTab) {
    case 'quotes': return docType === 'QUOTE';
    case 'photos': return docType === 'PHOTO';
    case 'other': return !['QUOTE', 'PHOTO'].includes(docType);
    default: return true;
  }
};

const filterDocumentsByTab = (documents: EntityDocumentView[], filterTab: DocumentTypeFilter): EntityDocumentView[] =>
  filterTab === 'all' ? documents : documents.filter(doc => matchesFilterTab(doc, filterTab));

interface DocumentTypeCounts {
  quotesCount: number;
  photosCount: number;
  otherCount: number;
}

const countDocumentsByType = (documents: EntityDocumentView[]): DocumentTypeCounts =>
  documents.reduce<DocumentTypeCounts>((counts, doc) => {
    const docType = normalizeDocumentType(doc.documentType);
    if (docType === 'QUOTE') return { ...counts, quotesCount: counts.quotesCount + 1 };
    if (docType === 'PHOTO') return { ...counts, photosCount: counts.photosCount + 1 };
    return { ...counts, otherCount: counts.otherCount + 1 };
  }, { quotesCount: 0, photosCount: 0, otherCount: 0 });

const toGridRow = (doc: EntityDocumentView): DocumentGridRow => ({
  id: doc.id,
  fileName: doc.fileName,
  extension: doc.extension,
  uploadBy: doc.uploadedBy || 'Unknown',
  uploadDate: doc.uploadedDate || '',
  uploadDateFormatted: formatDate(doc.uploadedDate),
  fileType: getFileTypeLabel(doc.extension),
  iconEmoji: getFileIcon(doc.extension),
  documentType: doc.documentType || 'General',
  document: doc
});

interface MessagePanelProps {
  severity: 'warning' | 'error' | 'info';
  title: string;
  subtitle: string;
  sx?: React.ComponentProps<typeof Alert>['sx'];
}

const MessagePanel: React.FC<MessagePanelProps> = ({ severity, title, subtitle, sx }) => (
  <Box sx={{ p: 4, textAlign: 'center' }}>
    <Alert severity={severity} sx={sx}>
      <Typography variant="body1" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ mt: 1, color: '#64748b' }}>
        {subtitle}
      </Typography>
    </Alert>
  </Box>
);

const NoEntitySelectedMessage: React.FC = () => (
  <MessagePanel severity="warning" title="No record selected" subtitle="Please select a record to view documents" />
);

const DocumentsErrorMessage: React.FC<{ error?: unknown }> = ({ error }) => (
  <MessagePanel
    severity="error"
    title="Error loading documents"
    subtitle={(error as { data?: { message?: string } })?.data?.message || 'Unable to load documents'}
  />
);

const DocumentsLoadingMessage: React.FC = () => (
  <Box sx={{ p: 4, textAlign: 'center' }}>
    <Typography variant="body1" sx={{ color: '#64748b' }}>
      Loading documents...
    </Typography>
  </Box>
);

const NoDocumentsMessage: React.FC = () => (
  <MessagePanel
    severity="info"
    title="No documents uploaded"
    subtitle='Click "Upload Documents" to add the first document'
    sx={{
      backgroundColor: '#f0f9ff',
      border: '1px solid #bae6fd',
      '& .MuiAlert-icon': {
        color: '#0284c7'
      }
    }}
  />
);

const NoDocumentsInCategoryMessage: React.FC = () => (
  <MessagePanel severity="info" title="No documents in this category" subtitle="Try selecting a different filter or upload new documents" />
);

const FileNameCell: React.FC<{ row: DocumentGridRow }> = ({ row }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.25, minWidth: 0 }}>
    <span style={{ fontSize: '0.9rem', flexShrink: 0 }}>{row.iconEmoji}</span>
    <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          cursor: 'pointer',
          color: '#1e40af',
          fontSize: '0.75rem',
          lineHeight: 1.2,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: '100%',
          '&:hover': { textDecoration: 'underline' }
        }}
      >
        {row.fileName}
      </Typography>
      <Typography variant="caption" sx={{
        color: '#6b7280',
        fontSize: '0.65rem',
        lineHeight: 1.1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        display: 'block'
      }}>
        {row.fileType}
      </Typography>
    </Box>
  </Box>
);

const UploadInfoCell: React.FC<{ row: DocumentGridRow }> = ({ row }) => (
  <Box sx={{ py: 0.25 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
      <Person sx={{ fontSize: 12, color: '#6b7280' }} />
      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.75rem', lineHeight: 1.2 }}>
        {row.uploadBy}
      </Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <CalendarToday sx={{ fontSize: 12, color: '#6b7280' }} />
      <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.7rem', lineHeight: 1.1 }}>
        {formatDate(row.uploadDate)?.split(',')[0]}
      </Typography>
    </Box>
  </Box>
);

const DocumentTypeChip: React.FC<{ type: string }> = ({ type }) => {
  const colors = getDocumentTypeColor(type);
  return (
    <Chip
      icon={getDocumentTypeIcon(type)}
      label={type}
      size="small"
      sx={{
        backgroundColor: colors.bg,
        color: colors.color,
        fontWeight: 600,
        fontSize: '0.65rem',
        height: '22px',
        '& .MuiChip-icon': {
          color: colors.color
        }
      }}
    />
  );
};

const ExtensionBadge: React.FC<{ extension: string }> = ({ extension }) => (
  <Box sx={{
    display: 'inline-block',
    px: 0.75,
    py: 0.25,
    backgroundColor: '#dbeafe',
    color: '#1e40af',
    borderRadius: 0.5,
    fontSize: '0.65rem',
    fontWeight: 600,
    minWidth: '32px',
    textAlign: 'center'
  }}>
    {extension.toUpperCase().slice(1)}
  </Box>
);

const SORT_SETTINGS: SortSettingsModel = {
  columns: [{ field: 'uploadDate', direction: 'Descending' }]
};

const PAGE_SETTINGS: PageSettingsModel = {
  pageSize: 15,
  pageSizes: [10, 15, 25, 50],
  currentPage: 1
};

const GRID_CONTAINER_SX = {
  width: '800px',
  maxWidth: '800px',
  '& .e-grid .e-content': {
    backgroundColor: '#ffffff',
  },
  '& .e-grid .e-headercontent': {
    backgroundColor: '#f8fafc',
    borderBottom: '2px solid #e5e7eb',
  },
  '& .e-grid .e-headercelldiv': {
    fontWeight: 600,
    color: '#374151',
    fontSize: '0.8rem',
    padding: '4px 8px',
  },
  '& .e-grid .e-rowcell': {
    padding: '2px 8px',
    fontSize: '0.8rem',
    lineHeight: 1.3,
  },
  '& .e-grid .e-row': {
    minHeight: '40px',
    height: '40px',
    borderBottom: '1px solid #f3f4f6',
  },
  '& .e-grid .e-row:hover': {
    backgroundColor: '#f0f9ff',
    cursor: 'pointer',
  },
  '& .e-grid .e-row.e-altrow:hover': {
    backgroundColor: '#f0f9ff',
  },
  '& .e-pager': {
    backgroundColor: '#ffffff',
    borderTop: '1px solid #e5e7eb',
    minHeight: '36px',
  },
  '& .e-pager .e-numericcontainer .e-link': {
    fontSize: '0.8rem',
    padding: '4px 8px',
  },
};

interface DocumentTabsBarProps {
  filterTab: DocumentTypeFilter;
  onChange: (value: DocumentTypeFilter) => void;
  totalCount: number;
  counts: DocumentTypeCounts;
}

const DocumentTabsBar: React.FC<DocumentTabsBarProps> = ({ filterTab, onChange, totalCount, counts }) => (
  <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
    <Tabs
      value={filterTab}
      onChange={(_e, newValue) => onChange(newValue)}
      sx={{
        '& .MuiTab-root': {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem'
        }
      }}
    >
      <Tab label={`All Documents (${totalCount})`} value="all" />
      <Tab label={`Quotes (${counts.quotesCount})`} value="quotes" />
      <Tab label={`Photos (${counts.photosCount})`} value="photos" />
      <Tab label={`Other (${counts.otherCount})`} value="other" />
    </Tabs>
  </Box>
);

interface DocumentsDataTableProps {
  className?: string;
  gridData: DocumentGridRow[];
  onRowSelected: (args: RowSelectEventArgs) => void;
}

const DocumentsDataTable: React.FC<DocumentsDataTableProps> = ({ className, gridData, onRowSelected }) => (
  <Box className={className} sx={GRID_CONTAINER_SX}>
    <GridComponent
      dataSource={gridData}
      allowPaging={true}
      allowSorting={true}
      sortSettings={SORT_SETTINGS}
      pageSettings={PAGE_SETTINGS}
      rowSelected={onRowSelected}
      height="280px"
      width="800px"
      enableHover={true}
      rowHeight={40}
    >
      <ColumnsDirective>
        <ColumnDirective
          field="fileName"
          headerText="Document"
          width="30%"
          template={(props: DocumentGridRow) => <FileNameCell row={props} />}
          allowSorting={true}
        />
        <ColumnDirective
          field="documentType"
          headerText="Doc Type"
          width="15%"
          textAlign="Center"
          allowSorting={true}
          template={(props: DocumentGridRow) => <DocumentTypeChip type={props.documentType} />}
        />
        <ColumnDirective
          field="extension"
          headerText="File Type"
          width="10%"
          textAlign="Center"
          allowSorting={true}
          template={(props: DocumentGridRow) => <ExtensionBadge extension={props.extension} />}
        />
        <ColumnDirective
          field="uploadBy"
          headerText="Upload Info"
          width="45%"
          template={(props: DocumentGridRow) => <UploadInfoCell row={props} />}
          allowSorting={true}
        />
        <ColumnDirective
          field="uploadDate"
          headerText="Date"
          width="15%"
          type="date"
          format="yMd"
          textAlign="Center"
          allowSorting={true}
          allowFiltering={true}
          visible={false} // Hidden, used for sorting
        />
      </ColumnsDirective>
      <Inject services={[Page, Sort]} />
    </GridComponent>
  </Box>
);

export const DocumentsGrid: React.FC<DocumentsGridProps> = ({
  entityId,
  documents,
  isLoading,
  isError,
  error,
  currentUserName,
  onDownload,
  onDelete,
  className,
  readOnly = false
}) => {
  const [selectedDocument, setSelectedDocument] = useState<EntityDocumentView | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [filterTab, setFilterTab] = useState<DocumentTypeFilter>('all');

  const documentsArray = useMemo(() => documents || [], [documents]);

  const filteredDocuments = useMemo(
    () => filterDocumentsByTab(documentsArray, filterTab),
    [documentsArray, filterTab]
  );

  const gridData: DocumentGridRow[] = useMemo(
    () => filteredDocuments.filter(doc => !doc.isDeleted).map(toGridRow),
    [filteredDocuments]
  );

  const onRowSelected = (args: RowSelectEventArgs) => {
    const selectedRow = args.data as DocumentGridRow;
    setSelectedDocument(selectedRow.document);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedDocument(null);
  };

  if (!entityId) {
    return <NoEntitySelectedMessage />;
  }

  if (isError) {
    return <DocumentsErrorMessage error={error} />;
  }

  if (isLoading) {
    return <DocumentsLoadingMessage />;
  }

  if (!documentsArray || documentsArray.length === 0) {
    return <NoDocumentsMessage />;
  }

  const counts = countDocumentsByType(documentsArray);

  return (
    <>
      <DocumentTabsBar
        filterTab={filterTab}
        onChange={setFilterTab}
        totalCount={documentsArray.length}
        counts={counts}
      />

      {filteredDocuments.length === 0 ? (
        <NoDocumentsInCategoryMessage />
      ) : (
        <DocumentsDataTable className={className} gridData={gridData} onRowSelected={onRowSelected} />
      )}

      <ViewDocumentDialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        document={selectedDocument}
        currentUserName={currentUserName}
        onDownload={onDownload}
        onDelete={onDelete}
        readOnly={readOnly}
      />
    </>
  );
};

export default DocumentsGrid;
